import React, { useMemo } from 'react';
import { Browser } from '@capacitor/browser';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex, Heading, useColorModeValue, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  CATCH_POWER_MANUFACTURER_ID,
  ENERGY_MASTERS_TENANT_ID,
} from 'clipsal-cortex-utils/src/constants/common-constants';

import { ENERGY_MASTERS_FAQ_LINK } from '../../../common/constants';
import { useViewportType } from '../../../common/hooks/use-viewport-type';
import {
  APP_FEEDBACK_URL,
  METER_CATCH_TROUBLESHOOTING_ARTICLE_URL,
  METER_WATTWATCHERS_TROUBLESHOOTING_ARTICLE_URL,
} from '../../site/device-alerts/constants';
import { selectMeterByManufacturerId, selectSite } from '../../site/siteSlice';
import { SupportModal } from '../../support/SupportModal';
import { ProfilePageBase } from '../ProfilePageBase';

export const Contact = () => {
  const background = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.300', 'black');
  const hoverColor = useColorModeValue('whiteAlpha.700', 'black');
  const { isOpen: isSupportDialogOpen, onOpen: onOpenSupportDialog, onClose: onCloseSupportDialog } = useDisclosure();
  const navigate = useNavigate();
  const isCatchMeter = !!useSelector(selectMeterByManufacturerId(CATCH_POWER_MANUFACTURER_ID));
  const site = useSelector(selectSite);
  const isEnergyMastersSite = site.tenant_id === ENERGY_MASTERS_TENANT_ID;
  const { isDesktopViewport } = useViewportType();

  const contactLinkConfig = useMemo(() => {
    const linkConfigs = [
      {
        title: 'Live Page is not working',
        dataTestId: 'live-page-not-working-btn',
        onClick: () =>
          Browser.open({
            url: isCatchMeter
              ? METER_CATCH_TROUBLESHOOTING_ARTICLE_URL
              : METER_WATTWATCHERS_TROUBLESHOOTING_ARTICLE_URL,
            windowName: isDesktopViewport ? '_blank' : '_self',
          }),
      },
      {
        title: 'Issues with appliances installed under Energy Masters',
        dataTestId: 'installation-issue-btn',
        onClick: () => navigate('installation-issues'),
      },
    ];

    // If the site is Energy Masters, add the FAQs link
    if (isEnergyMastersSite) {
      linkConfigs.push({
        title: 'Energy Masters FAQs',
        dataTestId: 'energy-masters-faqs-btn',
        onClick: () =>
          Browser.open({
            url: ENERGY_MASTERS_FAQ_LINK,
            windowName: isDesktopViewport ? '_blank' : '_self',
          }),
      });
    }

    linkConfigs.push(
      ...[
        {
          title: `${isEnergyMastersSite ? 'Energy Masters' : 'Contact'} Support`,
          dataTestId: 'contact-support-btn',
          onClick: isEnergyMastersSite ? () => navigate('support-details') : onOpenSupportDialog,
        },
        {
          title: 'App Feedback',
          dataTestId: 'app-feedback-btn',
          onClick: () =>
            Browser.open({
              url: APP_FEEDBACK_URL,
              windowName: isDesktopViewport ? '_blank' : '_self',
            }),
        },
      ]
    );

    return linkConfigs;
  }, [isCatchMeter, isDesktopViewport, isEnergyMastersSite, navigate, onOpenSupportDialog]);

  return (
    <ProfilePageBase title={'Contact & Support'}>
      {contactLinkConfig.map((page) => (
        <Box bg={background} _hover={{ bg: hoverColor }} key={page.title}>
          <Flex
            borderBottom="1px solid"
            borderColor={borderColor}
            justify={'space-between'}
            align={'center'}
            py={8}
            px={6}
            cursor={'pointer'}
            onClick={page.onClick}
            data-testid={page.dataTestId}
          >
            <Heading fontSize={18} pr={4}>
              {page.title}
            </Heading>
            <ChevronRightIcon w={'26px'} h={'26px'} />
          </Flex>
        </Box>
      ))}

      {isSupportDialogOpen && <SupportModal isOpen={isSupportDialogOpen} onClose={onCloseSupportDialog} />}
    </ProfilePageBase>
  );
};
